import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { AccessibleChip } from '../../../../../components/inputs/AccessibleChip';
import { AccessibleAutocomplete } from '../../../../../components/inputs/AccessibleAutocomplete';
import { StyledInputLabel } from '../../../../../components/inputs/InputLabel';
import { StyledQuestionMark } from '../../../../../components/QuestionMark';
import { Collections } from '../../../../../types/Branding.types';

type CollectionOption = { value: string; label: string };

type CollectionsFieldProps = {
  value: string[];
  collections?: Collections;
  disabled?: boolean;
  onChange: (values: string[]) => void;
};

export const CollectionsField = ({
  value,
  collections,
  disabled = false,
  onChange,
}: CollectionsFieldProps) => {
  const [pendingOption, setPendingOption] = useState<CollectionOption | null>(
    null
  );

  if (!collections || Object.keys(collections).length === 0) {
    return null;
  }

  const allOptions: CollectionOption[] = useMemo(() => {
    return Object.entries(collections).map(([key, collection]) => ({
      value: key,
      label: collection.name,
    }));
  }, [collections]);

  const availableOptions = useMemo(() => {
    return allOptions.filter((opt) => !value.includes(opt.value));
  }, [allOptions, value]);

  const selectedOptions = useMemo(() => {
    return value
      .map((key) => allOptions.find((opt) => opt.value === key))
      .filter((opt): opt is CollectionOption => opt !== undefined);
  }, [value, allOptions]);

  const handleAdd = () => {
    if (!pendingOption) return;
    onChange([...value, pendingOption.value]);
    setPendingOption(null);
  };

  const handleRemove = (key: string) => {
    onChange(value.filter((v) => v !== key));
  };

  const labelId = 'collections-label';
  const inputId = 'collections-input';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <StyledInputLabel id={labelId} htmlFor={inputId}>
          Collections
          <Tooltip
            title="Select which collections have access to this agreement"
            placement="top"
          >
            <IconButton
              size="small"
              sx={{ marginLeft: '3px' }}
              aria-label="Select which collections have access to this agreement"
            >
              <StyledQuestionMark fontSize="small" />
            </IconButton>
          </Tooltip>
        </StyledInputLabel>
      </Box>
      <Box sx={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <FormControl sx={{ flex: 1, maxWidth: { lg: '40%' } }}>
          <AccessibleAutocomplete
            id={inputId}
            options={availableOptions}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={pendingOption}
            onChange={(_e, val) => setPendingOption(val)}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                inputProps={{
                  ...params.inputProps,
                  'aria-labelledby': labelId,
                }}
                placeholder="Select a collection"
              />
            )}
          />
        </FormControl>
        <Button
          variant="primary"
          onClick={handleAdd}
          disabled={disabled || !pendingOption}
          startIcon={<AddIcon />}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Add collection
        </Button>
      </Box>
      {selectedOptions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mt: 8 }}>
          {selectedOptions.map((opt) => (
            <AccessibleChip
              key={opt.value}
              label={opt.label}
              onDelete={disabled ? undefined : () => handleRemove(opt.value)}
              disabled={disabled}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
